/**
 * RealmTile.tsx — one realm panel. Drop target for cards from any zone;
 * displays placements (Wonders/Lands/Items) split by owner; shows the
 * stone marker and lets the controller seize/release manually.
 */

import { useDroppable } from '@dnd-kit/core';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { type CardData, type Placement, type PlayerId, REALMS, type RealmName } from '../types.js';
import { Card } from './Card.js';

type Props = {
  realm: RealmName;
  placements: readonly Placement[];
  stone: PlayerId | null;
  viewer: PlayerId;
  opponent: PlayerId;
  cards: ReadonlyMap<string, CardData>;
  imageUrl: (cardId: string) => string;
  active?: boolean | undefined;
  /** Realms open one per round in lane order. When false the lane is
   *  visually locked and the drop target is disabled. */
  isOpen?: boolean | undefined;
  onPlacementContextMenu?: ((cardId: string, e: React.MouseEvent) => void) | undefined;
  onSeizeStone?: ((player: PlayerId | null) => void) | undefined;
};

// Realm lanes are tinted-but-translucent so a playmat (tabletop art)
// shows through. A vertical gradient + inner-shadow glow gives depth;
// the bordered outline is supplied by the wrapper className. Each
// realm's hue is faint enough to blend with art behind the lane.
const REALM_BG: Record<RealmName, string> = {
  Petraia: 'bg-gradient-to-b from-petraia/15 via-petraia/5 to-petraia/15',
  Solfera: 'bg-gradient-to-b from-solfera/15 via-solfera/5 to-solfera/15',
  Thalwind: 'bg-gradient-to-b from-thalwind/15 via-thalwind/5 to-thalwind/15',
  Umbrathene: 'bg-gradient-to-b from-umbrathene/15 via-umbrathene/5 to-umbrathene/15',
  Heliosynth: 'bg-gradient-to-b from-heliosynth/15 via-heliosynth/5 to-heliosynth/15',
  Boundless: 'bg-gradient-to-b from-boundless/15 via-boundless/5 to-boundless/15',
  Clear: 'bg-gradient-to-b from-clear/15 via-clear/5 to-clear/15',
};

// Border tint per realm. Provides the "clear borders" cue the user
// asked for without overpowering the playmat art behind the lane.
const REALM_BORDER: Record<RealmName, string> = {
  Petraia: 'ring-petraia/50',
  Solfera: 'ring-solfera/50',
  Thalwind: 'ring-thalwind/50',
  Umbrathene: 'ring-umbrathene/50',
  Heliosynth: 'ring-heliosynth/50',
  Boundless: 'ring-boundless/50',
  Clear: 'ring-clear/50',
};

export function RealmTile({
  realm,
  placements,
  stone,
  viewer,
  opponent,
  cards,
  imageUrl,
  active,
  isOpen = true,
  onPlacementContextMenu,
  onSeizeStone,
}: Props) {
  // Drops always accepted regardless of open/closed state — sandbox
  // testers can place into any lane. The closed visual is a hint, not
  // an enforcement gate. (Ranked mode handles legality at the rule
  // engine layer; the table-level droppable is intentionally open.)
  const { isOver, setNodeRef } = useDroppable({
    id: `realm-${realm}`,
    data: { kind: 'realm', realm, owner: viewer },
  });

  // Stone visualization position: when uncontested it sits at the
  // dividing line between the two halves; when seized by you it floats
  // to the TOP of YOUR section (per design: "should move from the
  // middle to the top of my lane"); when seized by opponent it floats
  // to the BOTTOM of THEIR section (mirror).
  const stonePos = stone === viewer ? 'self-top' : stone === opponent ? 'opp-bottom' : 'middle';

  return (
    <div
      ref={setNodeRef}
      className={clsx(
        // Vertical lane filling the container height. Translucent
        // realm-tinted gradient lets a playmat backdrop show through;
        // ring-2 gives the "clear border" cue. Closed realms render
        // greyed-out and accept no drops. Backdrop-blur was previously
        // applied here but caused full-tile re-rasterization on every
        // drag frame as cards crossed the surface — removed for
        // dragging stability; the gradient still reads as semi-transparent.
        'relative flex h-full flex-col rounded-tile px-2 py-2 shadow-inner ring-2',
        isOpen
          ? clsx(REALM_BG[realm] ?? 'bg-cosmos-700/20', REALM_BORDER[realm] ?? 'ring-cosmos-700/60')
          : 'bg-cosmos-950/60 opacity-60 ring-cosmos-800/40',
        active && 'realm-active',
        isOver && 'drop-target-active',
      )}
    >
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            key="closed-overlay"
            className="-translate-x-1/2 pointer-events-none absolute top-2 left-1/2 z-[6] flex flex-col items-center gap-1 rounded bg-cosmos-950/80 px-2 py-1 ring-1 ring-cosmos-700"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16, scale: 0.9 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
          >
            <span aria-hidden="true" className="text-base">
              🔒
            </span>
            <span className="font-mono text-[9px] uppercase tracking-wide text-cosmos-400">
              R{REALMS.indexOf(realm) + 1}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Status pill — top-right corner, indicates ownership at a glance. */}
      <span
        className={clsx(
          'absolute right-1 top-1 z-[5] rounded px-1.5 py-0.5 font-mono text-[9px]',
          stone === viewer && 'bg-amber-700/70 text-amber-100',
          stone === opponent && 'bg-rose-700/70 text-rose-100',
          stone === null && 'bg-cosmos-700/70 text-cosmos-300',
        )}
      >
        {stone === viewer ? 'YOURS' : stone === opponent ? 'OPP' : 'open'}
      </span>

      {/* Two scroll zones split by owner — opp on top, you on bottom.
          Within each owner's zone, placements are grouped into three
          sub-rows by card_type (Wonder · Land · Item) so the player
          can see at a glance what's anchored, what's growing the
          economy, and what's a transient buff. */}
      <div className="relative flex-1 overflow-y-auto border-rose-500/30 border-b pb-2">
        <span className="-translate-x-1/2 absolute top-1 left-1/2 z-[5] font-mono text-[9px] text-rose-400/70 uppercase tracking-wide">
          opp
        </span>
        <TypeGroupedLane
          ownerKey="opp"
          ringClass="ring-rose-500/60"
          realm={realm}
          placements={placements.filter((p) => p.owner !== viewer)}
          cards={cards}
          imageUrl={imageUrl}
          onPlacementContextMenu={onPlacementContextMenu}
        />
      </div>

      <div className="relative flex-1 overflow-y-auto border-amber-500/30 border-t pt-2">
        <span className="-translate-x-1/2 absolute bottom-1 left-1/2 z-[5] font-mono text-[9px] text-amber-400/70 uppercase tracking-wide">
          you
        </span>
        <TypeGroupedLane
          ownerKey="you"
          ringClass="ring-amber-500/60"
          realm={realm}
          placements={placements.filter((p) => p.owner === viewer)}
          cards={cards}
          imageUrl={imageUrl}
          onPlacementContextMenu={onPlacementContextMenu}
        />
      </div>

      {/* Stone disc + seize affordance — pinned to the dividing line
          between the two halves. Animates upward when you seize, downward
          when opponent seizes. Per design: claimed stone moves OUT OF
          the centre and INTO the seizing player's half (top of yours
          when you control). */}
      <button
        type="button"
        onClick={() => onSeizeStone?.(stone === viewer ? null : viewer)}
        title={
          stone === null
            ? 'Click to seize this realm'
            : stone === viewer
              ? "You control this realm's stone — click to release"
              : 'Opponent controls this stone'
        }
        className={clsx(
          'absolute left-1/2 z-10 h-9 w-9 -translate-x-1/2 rounded-full ring-2 transition-all duration-300',
          stonePos === 'middle' && 'top-[calc(50%-18px)] ring-cosmos-500/60',
          stonePos === 'self-top' && 'top-[calc(50%+4px)] ring-amber-300/80',
          stonePos === 'opp-bottom' && 'top-[calc(50%-40px)] ring-rose-300/80',
        )}
        style={{
          backgroundImage: `url('/assets/wonders/stone-${realm.toLowerCase()}.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <span className="sr-only">Seize stone</span>
      </button>
    </div>
  );
}

/**
 * One owner's half of a realm, split into Wonder / Land / Item rows.
 * Each row shows just the cards of that type (no row at all if the
 * type is empty). When the owner has zero placements total we render
 * a single "empty" placeholder.
 *
 * Reads `card_type` off CardData from the cards endpoint; older
 * snapshots may have null, in which case the placement falls into a
 * generic "Other" row at the bottom.
 */
function TypeGroupedLane({
  ownerKey,
  ringClass,
  realm,
  placements,
  cards,
  imageUrl,
  onPlacementContextMenu,
}: {
  ownerKey: 'opp' | 'you';
  ringClass: string;
  realm: RealmName;
  placements: readonly Placement[];
  cards: ReadonlyMap<string, CardData>;
  imageUrl: (cardId: string) => string;
  onPlacementContextMenu?: ((cardId: string, e: React.MouseEvent) => void) | undefined;
}) {
  if (placements.length === 0) {
    return (
      <div className="flex flex-wrap content-start justify-center gap-1 pt-4 pb-4">
        <span className="text-[10px] text-cosmos-600 italic">empty</span>
      </div>
    );
  }
  type Bucket = { label: string; key: 'Wonder' | 'Land' | 'Item' | 'Other'; items: Placement[] };
  const groups: Bucket[] = [
    { label: 'Wonders', key: 'Wonder', items: [] },
    { label: 'Lands', key: 'Land', items: [] },
    { label: 'Items', key: 'Item', items: [] },
    { label: 'Other', key: 'Other', items: [] },
  ];
  for (const p of placements) {
    const t = cards.get(p.cardId)?.card_type ?? '';
    const bucket: Bucket | undefined =
      t === 'Wonder' ? groups[0] : t === 'Land' ? groups[1] : t === 'Item' ? groups[2] : groups[3];
    bucket?.items.push(p);
  }
  return (
    <div className="flex flex-col gap-1 px-1 pt-4 pb-4">
      {groups
        .filter((g) => g.items.length > 0)
        .map((g) => (
          <div key={g.key}>
            <div className="mb-0.5 font-mono text-[8px] text-cosmos-500 uppercase tracking-wide">
              {g.label}
            </div>
            <div className="flex flex-wrap content-start justify-center gap-1">
              {g.items.map((p) => (
                <Card
                  key={`${ownerKey}-${p.cardId}-${p.x}-${p.y}`}
                  cardId={p.cardId}
                  card={cards.get(p.cardId)}
                  imageUrl={imageUrl(p.cardId)}
                  placement={p}
                  draggableId={`realm-${realm}-${ownerKey}-${p.cardId}-${p.x}-${p.y}`}
                  dragData={{ cardId: p.cardId, source: { kind: 'realm', realm, owner: p.owner } }}
                  onContextMenu={
                    onPlacementContextMenu ? (e) => onPlacementContextMenu(p.cardId, e) : undefined
                  }
                  size="md"
                  className={`ring-2 ${ringClass}`}
                />
              ))}
            </div>
          </div>
        ))}
    </div>
  );
}
